export {
  type ActingNotePart,
  actingNote,
  answeringLineLabel,
  fillRepresentativeTemplate,
  isExpandedRepresentativeLine,
  joinRepresentativeNames,
  memberLineAttribution,
  onBehalfOfListText,
  onBehalfOfText,
  representativeWithRole,
} from "./answering-attribution";
export type {
  AssessmentCombinedSourceStatus,
  AssessmentModeSourceStatus,
  AssessmentSimpleSourceStatus,
  AssessmentSourceMeasure,
  AssessmentSourceMode,
  AssessmentSourceStatus,
  ParsedProductAssessmentSource,
  ProductAssessmentSourceRead,
  ScoreRange,
} from "./assessment-condition-sources";
export {
  ASSESSMENT_COMBINED_STATUSES,
  ASSESSMENT_MODE_STATUSES,
  ASSESSMENT_SIMPLE_STATUSES,
  buildProductAssessmentScoreSource,
  buildProductAssessmentStatusSource,
  configuredAssessmentSourceModes,
  hasScoreSource,
  isDanglingProductAssessmentSource,
  isScoreConditionSource,
  isScoreSourceOperator,
  listProductAssessmentSources,
  malformedRuleProblem,
  matchScoreCondition,
  menuConditionOperator,
  operatorForNewSource,
  parseProductAssessmentSource,
  parseSeededScoreRange,
  productAssessmentSourceFieldKey,
  productAssessmentSourceStatuses,
  readProductAssessmentSource,
  readQuizScoreRange,
  resolveProductAssessmentConditionValues,
  resolveQuizConditionValues,
  SCORE_SOURCE_OPERATORS,
  scalarConditionOperators,
  scoreRangeOf,
  scoreSourceOperator,
  seedScoreRange,
} from "./assessment-condition-sources";
export {
  addMonthsUtc,
  forecastStartDate,
  lastDayOfPreviousMonth,
  parseYearMonth,
} from "./calendar-math";
export type { CollectionCurrency } from "./collection-currencies";
export { COLLECTION_CURRENCIES, getCurrencyLabel } from "./collection-currencies";
export type {
  ActivationCriterion,
  CollectionRequirementSettingKey,
  CollectionRequirementSettings,
  RequirementGovernedComponentType,
} from "./collection-requirements";
export {
  getActiveActivationCriteria,
  getAllowedComponentTypes,
  getDisallowedComponentTypes,
  getEnabledSnapshotTypes,
  getSettingForComponentType,
  isComponentTypeAllowed,
  REQUIREMENT_GOVERNED_COMPONENT_TYPES,
  REQUIREMENT_GOVERNED_SNAPSHOT_TYPES,
  REQUIREMENT_POLICY,
} from "./collection-requirements";
export type { ConditionRuleCheckContext, ConditionRuleProblem } from "./condition-rule-problem";
export { findConditionRuleProblem } from "./condition-rule-problem";
export type { ConditionalDisplayConfig, ConditionalOperator } from "./conditional-display";
export {
  compareFieldCondition,
  isValueRequiredOperator,
  VALUE_REQUIRED_OPERATORS,
} from "./conditional-display";
export type {
  ConditionScope,
  PortfolioModelDeviation,
  RiskScoreCapState,
} from "./conditional-sources";
export {
  CLASSIFICATION_TYPE_SOURCE,
  CLIENT_COUNTRY_SOURCE,
  CLIENT_RECORD_SOURCES,
  CLIENT_TYPE_SOURCE,
  COMPONENT_PRODUCED_SOURCES,
  CUSTOM_FIELD_SOURCE_PREFIX,
  clientRecordConditionValue,
  EXPECTING_FINANCIAL_CHANGES_SOURCE,
  FLOW_STATUS_SOURCE,
  FOLLOWUP_MODE_SOURCE,
  followupModeConditionValue,
  GOAL_HAS_MANDATE_SOURCE,
  GOAL_HAS_RISK_SOURCE,
  GOAL_RISK_SCORE_CAPPED_SOURCE,
  GOAL_RISK_SCORE_SOURCE,
  GOAL_TYPE_NONE,
  GOAL_TYPE_SOURCE,
  isConditionAllowedInScope,
  isCustomFieldSource,
  isDanglingQuizSource,
  isProductAssessmentSource,
  isQuizResultSource,
  isQuizScoreSource,
  isSelfReferentialConditionSource,
  isSentinelSource,
  PORTFOLIO_HAS_MANDATE_CHECK_SOURCE,
  PORTFOLIO_MANDATE_STATUS_SOURCE,
  PORTFOLIO_MANDATE_STATUS_VALUES,
  PORTFOLIO_MODEL_DEVIATION_SOURCE,
  PORTFOLIO_MODEL_DEVIATION_VALUES,
  PORTFOLIO_SCHEDULE_SOURCE,
  PORTFOLIO_TYPE_SOURCE,
  PRODUCT_ASSESSMENT_SOURCE_PREFIX,
  pdfSectionScopeToConditionScope,
  QUIZ_RESULT_SOURCE_PREFIX,
  QUIZ_SCORE_SOURCE_PREFIX,
  quizIdFromResultSource,
  quizIdFromScoreSource,
  RISK_SCORE_CAP_STATES,
  resolveConditionSourceScope,
  SENTINEL_SOURCE_SCOPES,
  SUMSUB_DOCUMENT_COUNTRY_SOURCE,
  SUMSUB_VERIFIED_SOURCE,
  TRAPETS_PEP_SOURCE,
  TRAPETS_SANCTIONED_SOURCE,
} from "./conditional-sources";
export type { CostApiValue } from "./cost-api-keys";
export { parseCostApiKeys, pickCostApiValue } from "./cost-api-keys";
export type { CostPeriodEntry, HoldingPeriodCost } from "./cost-periods";
export { COST_SIMULATION_YEARS, foldCostPeriods } from "./cost-periods";
export { DEFAULT_CURRENCY, isCurrencyCode, STANDARD_TEMPLATE_CURRENCY } from "./currency-code";
export {
  arrangeDashboard,
  canSeeDashboardWidget,
  DASHBOARD_SECTION_COLUMNS,
  DASHBOARD_WIDGETS,
  DEFAULT_CASH_THRESHOLD,
  DEFAULT_DASHBOARD_WIDGETS,
  dashboardWidgetsFor,
  getDashboardWidget,
  isDashboardWidgetAllowed,
  isDashboardWidgetKey,
  resolveCashThreshold,
  resolveDashboardWidgets,
} from "./dashboard-widgets";
export { isPastDbTimestamp, parseDbTimestamp } from "./db-timestamp";
export { representativeRoleLabel } from "./dictionary/answering-roles";
export {
  ALPHA2_TO_ALPHA3,
  ALPHA3_TO_ALPHA2,
  COUNTRIES,
  getCountryFlag,
  getCountryName,
  getCountryNameWithFlag,
  isKnownAlpha2Code,
  MISC_COUNTRY_CODE,
  resolveCountryCode,
  toAlpha2,
  toAlpha3,
} from "./dictionary/countries";
export { formatEninKey } from "./dictionary/enin-roles";
export { getMonthName } from "./dictionary/months";
export { quizResultStatusLabel } from "./dictionary/quiz-status";
export { getSumsubRejectLabel } from "./dictionary/sumsub-reject-labels";
export { docSlugFromPageId } from "./doc-page-id";
export type { FeatureFlagKey } from "./feature-flags";
export {
  FEATURE_FLAG_METADATA,
  FEATURE_FLAGS,
  isFeatureEnabled,
} from "./feature-flags";
export type {
  FieldValueInvalidReason,
  FieldValueValidation,
  FlowOptionLabelSource,
  OptionFieldType,
  OptionLabelSource,
  SyncKeyResolution,
} from "./field-types";
export {
  canonicalizeDateTime,
  canonicalizeFlowOptionInput,
  formatFlowOptionFieldSyncKey,
  formatFlowOptionFieldValue,
  formatOptionFieldValue,
  isConditionSourceCustomFieldType,
  isOptionFieldType,
  isPresenceOnlyCustomFieldType,
  normalizeVisibleValue,
  OPTION_FIELD_TYPES,
  optionIdsJson,
  parseFieldOptions,
  parseMultiselectValue,
  parseNumberLenient,
  resolveFlowOptionLabel,
  resolveFlowOptionSyncKeyInput,
  resolveFlowOptionValue,
  resolveOptionLabel,
  validateFieldValue,
  valueContains,
  visibleFlowOptions,
} from "./field-types";
export {
  CAPACITY_TRIANGLE_KEYS,
  capacityInputFrequencyFor,
  capacityTriangleFromEntries,
  capacityVariantFor,
  computeCapacity,
  hasCapacityVariantEnabled,
  resolveLiquidityBufferMode,
  withoutWrongFrequencyCapacityKeys,
  wrongFrequencyCapacityKeys,
} from "./financial-situation-capacity";
export type {
  DuplicateSyncKey,
  FsFieldCategory,
  FsFieldLists,
  InapplicableEntry,
  InvalidEntryAmount,
  UnresolvedSyncKey,
} from "./financial-situation-fields";
export {
  applicableFieldsFor,
  applyTranslationDialog,
  FS_CATEGORIES,
  findDuplicateSyncKeys,
  findInapplicableEntries,
  findInvalidEntryAmounts,
  findInvalidLiquidityBufferMode,
  flowCurrencyMismatch,
  hasEnteredAmount,
  missingRequiredFieldIds,
  normalizeSyncKey,
  parseFieldList,
  requiredFieldIdsFor,
  resolveEntryTypeIds,
  resolveFieldLabel,
  sanitizeFieldDefinition,
  sanitizeFieldList,
} from "./financial-situation-fields";
export { toFlowCompletionSummary } from "./flow-completion-summary";
export type { FlowPageType, FlowPageVisibility } from "./flow-component-policy";
export {
  ALL_FLOW_COMPONENT_TYPES,
  ALL_PDF_COMPONENT_TYPES,
  isAdvisorOnlyComponent,
  isComponentAllowedOnPage,
  SESSION_DECISION_COMPONENT_TYPES,
} from "./flow-component-policy";
export {
  FLOW_MAX_GOALS_DEFAULT,
  FLOW_MAX_GOALS_MAX,
  FLOW_MAX_GOALS_MIN,
  GOAL_TABS_MAX_VISIBLE,
  goalLimitForFlow,
  isValidFlowMaxGoals,
} from "./flow-goal-limit";
export type { IframeAspectRatio, IframeUrlProblem } from "./flow-iframe-config";
export {
  IFRAME_ASPECT_RATIOS,
  IFRAME_HEIGHT_DEFAULT,
  IFRAME_HEIGHT_MAX,
  IFRAME_HEIGHT_MIN,
  IFRAME_URL_MAX_LENGTH,
  iframeUrlProblem,
  isIframeAspectRatio,
  isPlatformIframeUrl,
  isValidIframeHeight,
} from "./flow-iframe-config";
export type {
  ComponentRequirementFlags,
  RequirementEvaluationInput,
  RequirementEvaluationResult,
  RequirementSet,
  RequirementViolation,
  RequirementViolationKind,
} from "./flow-requirements";
export {
  deriveRequirementSet,
  evaluateRequirements,
  getComponentRequirements,
} from "./flow-requirements";
export type { FlowWorkAction, FlowWorkChannel } from "./flow-status";
export { canStartOrSubmitFlow } from "./flow-status";
export type { FollowupNamingItem } from "./followup-goal-name";
export { followupGoalName, followupName } from "./followup-goal-name";
export {
  followupComparisonVariant,
  isAccountFollowupPortfolio,
  isFollowupPortfolio,
  lockedScheduleType,
} from "./followup-lineage";
export { groupActingRepresentatives } from "./group-acting-representatives";
export {
  buildI18n,
  DEFAULT_LOCALE,
  ENABLED_LOCALES,
  FLOW_LANGUAGE_HEADER,
  getLocalizedArray,
  getLocalizedValue,
  isLocaleEnabled,
  isRTL,
  LOCALE_NAMES,
  LOCALE_NATIVE_NAMES,
  pickT,
  renameI18n,
  resolveLocale,
  SUPPORTED_LOCALES,
} from "./i18n";
export type { InstrumentDataset } from "./instrument-datasets";
export {
  DATASET_VENDOR_FLAG,
  enabledDatasetsFrom,
  INSTRUMENT_DATASETS,
} from "./instrument-datasets";
export { resolveFixedBuffer } from "./investment-capacity";
export type {
  InvalidIsinAmountLine,
  IsinAmountLine,
  IsinAmountLineError,
  ValidIsinAmountLine,
} from "./isin";
export {
  isNormalizedIsin,
  isValidIsin,
  isValidIsinAmountLine,
  normalizeIsin,
  parseIsinAmountLines,
} from "./isin";
export { ISO_DATE_PATTERN, isValidIsoDate, normalizeIsoDate } from "./iso-date";
export { frozenTotalsByProduct, type LockedHoldingCap, lockedHoldingCap } from "./locked-holding";
export { formatMandatePct } from "./mandate-format";
export type {
  MandateLineRuleCode,
  MandateLineRuleFigures,
  MandateLineRuleProblem,
} from "./mandate-lines";
export { MANDATE_PCT_TOLERANCE, mandateLineRuleProblem } from "./mandate-lines";
export type {
  ModelSelectionState,
  ModelSelectionStateWithType,
  PickRequestError,
  PickRequestOutcome,
} from "./model-portfolio-selection";
export {
  applyPickRequest,
  modelPortfolioDeviates,
  modelPortfolioDeviation,
  onPlatformChange,
  onPortfolioTypeChange,
  onRescore,
  portfolioUsedId,
} from "./model-portfolio-selection";
export {
  LOCALE_TO_MS_LANGUAGE_ID,
  localeToMsLanguageId,
  MS_DOCUMENT_TYPES,
  MS_DOCUMENT_TYPES_MAP,
  MS_LANGUAGES,
  MS_LANGUAGES_MAP,
  MS_MARKETS,
  MS_MARKETS_MAP,
  msLanguageIdToLocale,
  resolveBackfillLocale,
} from "./morningstar";
export { normalizeIssuer } from "./normalize-issuer";
export { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "./password-policy";
export { PATCH_MAX_INSTRUMENT_HISTORY_YEARS } from "./patch-instrument-policy";
export type { TenantTier, TierConfig } from "./payments/pricing";
export {
  calculateMonthlyAmount,
  calculateProration,
  formatAmount,
  TIER_CONFIG,
  validateSeats,
} from "./payments/pricing";
export type { Permission } from "./permissions";
export {
  hasPermission,
  hasPermissionType,
  isFlowInstanceReadOnlyForUser,
  PERMISSION_METADATA,
  PERMISSIONS,
} from "./permissions";
export { productFitsDynamicPortfolioPlatforms, productOnGoalPlatform } from "./platform-fit";
export {
  canAddToDynamicPortfolio,
  FULL_WEIGHT_TEN_THOUSANDTHS,
  hasStoredPrecision,
  isHoldableByDynamicPortfolio,
  isWeightBasedProduct,
  isWeightTotalComplete,
  PERCENT_ROUNDING_SCALE,
  PREFERENCE_MAXIMUM_LIMIT,
  sumTenThousandths,
  sumWeights,
  toTenThousandths,
} from "./portfolio-weights";
export type {
  DateFormat,
  DecimalSeparator,
  FormatNumberOptions,
  RegionalFormatOption,
  RegionalFormatSettings,
  RegionalFormatValidationError,
  ThousandSeparator,
} from "./regional-format";
export {
  coerceRegionalFormat,
  DATE_FORMAT_OPTIONS,
  DATE_FORMATS,
  DECIMAL_SEPARATOR_OPTIONS,
  DECIMAL_SEPARATORS,
  DEFAULT_REGIONAL_FORMAT,
  decimalSeparatorChar,
  formatDate,
  formatDateTime,
  formatNumber,
  formatZonedDate,
  formatZonedDateTime,
  getCalendarDateParts,
  getZonedDateParts,
  isDateFormat,
  isDecimalSeparator,
  isThousandSeparator,
  parseNumber,
  regionalFormatSample,
  THOUSAND_SEPARATOR_OPTIONS,
  THOUSAND_SEPARATORS,
  thousandSeparatorChar,
  validateRegionalFormat,
} from "./regional-format";
export type { AdjustmentResult } from "./risk-adjustment-scoring";
export {
  allAdjustmentQuestionsAnswered,
  answerDelta,
  computeAdjustment,
  enabledQuestions,
  strayAdjustmentAnswerIds,
  truncateAdjustment,
  unansweredAdjustmentQuestions,
} from "./risk-adjustment-scoring";
export type { RiskWriteField, RiskWriteTarget } from "./risk-input-scope";
export {
  changedRiskInputScopes,
  effectiveRiskInputs,
  isRiskInputScope,
  normalizeRiskInputScope,
  RISK_INPUT_SCOPE_KEYS,
  renderedRiskSections,
  renderedRiskSectionsInPdfSection,
  resolveRiskInputScopes,
  riskInputsCompleteness,
  riskPdfPlacementRendersNothing,
  riskPlacementRendersNothing,
  riskSectionsAnswered,
  riskWriteFieldScopes,
  riskWriteScopeMessage,
  riskWriteScopeViolations,
  riskWriteTargetOwnsNothing,
} from "./risk-input-scope";
export type { RiskWidgetSeed } from "./risk-widget-seed";
export { buildRiskWidgetSeed } from "./risk-widget-seed";
export { sanitizeRedirectUrl } from "./sanitize-redirect-url";
export { sanitizeReturnTo } from "./sanitize-return-to";
export type { RecurringScheduleDates } from "./schedule-defaults";
export {
  DEFAULT_RECURRING_HORIZON_YEARS,
  defaultScheduleEndDate,
  defaultScheduleStartDate,
  normalizeScheduleMonth,
  parseScheduleDate,
  recurringEndBeforeStart,
  resolveRecurringScheduleDates,
  SCHEDULE_MONTH_INPUT_HINT,
  SCHEDULE_MONTH_MAX_YEAR,
  SCHEDULE_MONTH_MIN_YEAR,
  scheduleMonthParts,
} from "./schedule-defaults";
export type { SsoSignInErrorKind } from "./sso-sign-in-errors";
export {
  classifySsoSignInError,
  isSsoSignInErrorKind,
  isSsoSignInErrorRef,
  SSO_ERROR_CODE_MAX_CHARS,
  SSO_ERROR_DESCRIPTION_MAX_CHARS,
  SSO_ERROR_LINK_PLACEHOLDER,
  SSO_ERROR_LOGIN_MESSAGE_MAX_CHARS,
  SSO_SIGN_IN_ERROR_KINDS,
  sanitizeSsoErrorCode,
  sanitizeSsoErrorDescription,
  sanitizeSsoErrorMessageForLogin,
} from "./sso-sign-in-errors";
export {
  classifyStandaloneNavigation,
  INTERNAL_REDIRECT_URL_PARAM,
  isFlowInstancePath,
  isStandaloneExitPath,
  isStandaloneLocation,
  mergeStandaloneParams,
  STANDALONE_PARAM,
  type StandaloneNavigationTarget,
} from "./standalone-flow";
export type {
  ProductSuitabilityMeta,
  ProductSuitabilityResult,
  SuitabilityClassification,
  SuitabilityContext,
  SuitabilityExperience,
  SuitabilityIssueType,
  SuitabilityStatus,
} from "./suitability";
export {
  bypassesSuitabilityChecks,
  checkProductSuitability,
  deriveSuitabilityStatus,
} from "./suitability";
export { isSumsubFinalAnswer } from "./sumsub-final-answer";
export type { StoredConditionRule } from "./tab-conditions";
export {
  carrierConditionRules,
  configConditionRules,
  hiddenLayoutComponentIds,
  hiddenTabIds,
  parseRuleCarrier,
} from "./tab-conditions";
export {
  buildRootOrigin,
  buildTenantOrigin,
  getTenantSlugHostError,
  getTenantSlugHostErrorCode,
  getWebRootDomainMismatch,
  isTenantPlatformOrigin,
  normalizeRootDomain,
  RESERVED_TENANT_SUBDOMAINS,
  resolveTenantHost,
  TENANT_SLUG_CHANGE_COOLDOWN_DAYS,
  TENANT_SLUG_QUARANTINE_DAYS,
  type TenantHostContext,
} from "./tenant-host";
export { isProtectedWebPath, isPublicWebPath, shouldPrefetchAppBootstrap } from "./web-routes";
export { isWrapperCompatibleWithSourceAccount } from "./wrapper-compatibility";
export {
  coerceTimeZone,
  DEFAULT_TENANT_TIME_ZONE,
  isValidTimeZone,
  todayInZone,
  zonedDateParts,
  zonedDateTimeParts,
  zonedMidnightToUtc,
} from "./zoned-dates";
